import type { ContractLinkSummary, ContractLinkType } from "@closer/shared";
import { CONTRACT_LINK_TYPE_NAMES, CONTRACT_LINK_TYPES } from "@closer/shared";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  FormField,
  Input,
  Modal,
  Select,
  Skeleton,
  useToast,
} from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router";

import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { contractPath } from "../../lib/contractRoutes";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * BAĞLI KAYITLAR — ek protokol, tadil, yenileme (CLOS-253 `[161]`).
 *
 * ### ⚠ Hedef, kaynağın YENİ SÜRÜMÜ DEĞİL
 *
 * Ayrı bir sözleşme doğuyor: kendi kimliği, kendi sürüm zinciri, kendi imza süreci.
 * Ekran da bunu böyle anlatıyor — satırlar ayrı kayda bağlantı veriyor, sürüm listesine
 * karışmıyor.
 *
 * ### ⚠ Yalnız İMZASI TAMAMLANMIŞ sözleşmeden
 *
 * Sunucunun kuralı; henüz imzalanmamış bir metne "ek protokol" yapmak, bağlayıcı olmayan
 * bir şeyi bağlayıcı saymak olurdu. Reddi kullanıcıya olduğu gibi gösteriyoruz.
 *
 * ### ⚠ Erişilemeyen karşı kaydın BAŞLIĞI gösterilmiyor
 *
 * Sunucu erişimi olmayana başlığı `null` döndürüyor; ekran o satırı "erişiminiz yok"
 * diye gösteriyor ve bağlantı vermiyor. İlişkinin varlığı görünür, kimliği görünmez.
 */
export function ContractLinksSection({
  contractId,
  kayitYazma = true,
}: {
  contractId: string;
  /** Görüntüleyenin bu kayıttaki yazma erişimi (CLOS-888). */
  kayitYazma?: boolean;
}) {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const [acik, setAcik] = useState(false);

  const anahtar = tenantKey(tenantId, "contract", contractId, "bagli-kayitlar");
  const iliskiler = useQuery({
    queryKey: anahtar,
    queryFn: async () =>
      (await api<ContractLinkSummary[]>(`/api/contracts/${contractId}/links`, { tenantId })).data,
  });

  if (!can("contract.view")) return null;

  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <h2 className="mr-auto text-base font-semibold text-fg-heading">Bağlı kayıtlar</h2>
        {can("contract.create") && kayitYazma ? (
          <Button type="button" size="sm" variant="secondary" onClick={() => setAcik(true)}>
            Ek protokol / tadil oluştur
          </Button>
        ) : null}
      </div>

      {iliskiler.isPending ? (
        <Skeleton lines={2} />
      ) : iliskiler.error ? (
        <StateView
          state={resolveErrorState(iliskiler.error, {
            idempotent: true,
            overrides: {
              error: { title: "Bağlı kayıtlar yüklenemedi", description: "Bölüm getirilemedi." },
            },
          })}
          onRetry={() => void iliskiler.refetch()}
        />
      ) : (iliskiler.data ?? []).length === 0 ? (
        <p className="text-sm text-fg-muted">
          Bu sözleşmeye bağlı ek protokol, tadil veya yenileme kaydı yok.
        </p>
      ) : (
        <ul className="flex flex-col">
          {(iliskiler.data ?? []).map((iliski) => {
            const kaynakBiziz = iliski.sourceContractId === contractId;
            const karsiKayit = kaynakBiziz ? iliski.targetContractId : iliski.sourceContractId;
            return (
              <li
                key={iliski.id}
                className="flex flex-wrap items-center justify-between gap-2 border-b border-border-default py-1.5 last:border-0"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-sm text-fg">
                    {/* ⚠ Erişim yoksa başlık YOK: sunucu null döndürüyor, ekran uydurmuyor. */}
                    {iliski.counterpartTitle ?? "Bu kayda erişiminiz yok"}
                  </span>
                  <span className="text-xs text-fg-soft">
                    {kaynakBiziz ? "Bu sözleşmeden doğdu" : "Bu sözleşme şundan doğdu"} ·{" "}
                    {new Date(iliski.createdAt).toLocaleDateString("tr-TR")} ·{" "}
                    {iliski.createdByName}
                    {iliski.copiedFields.length > 0
                      ? ` · kopyalanan: ${iliski.copiedFields
                          .map((a) => (a === "parties" ? "taraflar" : "temel bilgiler"))
                          .join(", ")}`
                      : ""}
                  </span>
                </span>
                <span className="flex items-center gap-2">
                  <Badge tone="neutral">{CONTRACT_LINK_TYPE_NAMES[iliski.linkType]}</Badge>
                  {iliski.counterpartTitle ? (
                    <Link
                      className="text-xs font-medium text-primary hover:underline"
                      to={contractPath(karsiKayit, null)}
                    >
                      Kaydı aç
                    </Link>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      <BagliKayitDialog
        open={acik}
        contractId={contractId}
        onClose={() => setAcik(false)}
        onDone={async () => {
          setAcik(false);
          await queryClient.invalidateQueries({ queryKey: anahtar });
        }}
      />
    </Card>
  );
}

/** Bağlı kayıt oluşturma — tür, başlık ve kopyalanacak alanlar. */
function BagliKayitDialog({
  open,
  contractId,
  onClose,
  onDone,
}: {
  open: boolean;
  contractId: string;
  onClose: () => void;
  onDone: () => Promise<void> | void;
}) {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();

  const [linkType, setLinkType] = useState<ContractLinkType>("amendment");
  const [title, setTitle] = useState("");
  const [taraflar, setTaraflar] = useState(true);
  const [temelBilgiler, setTemelBilgiler] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLinkType("amendment");
    setTitle("");
    setTaraflar(true);
    setTemelBilgiler(true);
  }, [open]);

  const olustur = useMutation({
    mutationFn: async () =>
      api(`/api/contracts/${contractId}/linked-contracts`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({
          linkType,
          title: title.trim(),
          copyFields: [...(taraflar ? ["parties"] : []), ...(temelBilgiler ? ["metadata"] : [])],
        }),
      }),
    onSuccess: async () => {
      toast.success("Bağlı kayıt oluşturuldu");
      await onDone();
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Bağlı kayıt oluşturulamadı"),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Bağlı kayıt oluştur"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            onClick={() => olustur.mutate()}
            loading={olustur.isPending}
            disabled={title.trim() === ""}
          >
            Oluştur
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-xs text-fg-soft">
          Yeni kayıt ayrı bir sözleşmedir: kendi sürüm zinciri ve kendi imza süreciyle başlar.
          Kaynak sözleşme değişmez.
        </p>

        <FormField label="İlişki türü">
          <Select
            id="bagli-tur"
            value={linkType}
            onChange={(olay) => setLinkType(olay.target.value as ContractLinkType)}
          >
            {CONTRACT_LINK_TYPES.map((t) => (
              <option key={t} value={t}>
                {CONTRACT_LINK_TYPE_NAMES[t]}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField label="Yeni kaydın başlığı">
          <Input
            id="bagli-baslik"
            value={title}
            onChange={(olay) => setTitle(olay.target.value)}
            placeholder="Örn. Tedarik Sözleşmesi — 1. Tadil"
          />
        </FormField>

        <fieldset className="flex flex-col gap-1">
          <legend className="text-sm font-medium text-fg-strong">Kopyalanacak alanlar</legend>
          <Checkbox
            label="Taraflar"
            checked={taraflar}
            onChange={(olay) => setTaraflar(olay.target.checked)}
          />
          <Checkbox
            label="Temel bilgiler"
            checked={temelBilgiler}
            onChange={(olay) => setTemelBilgiler(olay.target.checked)}
          />
          {/* ⚠ Kopyalanan değerler TASLAK: kaynakla canlı bağı yok, yeni kayıtta düzenlenir. */}
          <p className="text-xs text-fg-soft">
            Kopyalanan değerler yeni kayıtta düzenlenebilir taslak olarak başlar; kaynak kayıtla
            bağlantıları kalmaz.
          </p>
        </fieldset>
      </div>
    </Modal>
  );
}
